import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { COMMUNITY_TICKET_NOTES, OrderPricingOption } from './orderPricing';

const formatAmount = (amount: number) => `${Number(amount || 0).toLocaleString('vi-VN')} đ`;

const sampleOrders = [
    { code: '••••••••••', status: 'Đang chờ', progress: 0, amount: 'Theo bảng giá', color: 'bg-yellow-100 text-yellow-800' },
    { code: '••••••••••', status: 'Đang xử lý', progress: 60, amount: 'Theo bảng giá', color: 'bg-blue-100 text-blue-800' },
    { code: '••••••••••', status: 'Thành công', progress: 100, amount: 'Theo bảng giá', color: 'bg-green-100 text-green-800' },
];

const features = [
    {
        title: 'Theo dõi tiến độ',
        description: 'Xem trạng thái từng mã chung sức, số lượt đã xử lý và tiến độ cập nhật trực tiếp.',
        icon: '↗',
        color: 'bg-blue-50 text-blue-700',
    },
    {
        title: 'Sửa hoặc hủy mã đang chờ',
        description: 'Chỉnh mã, số lượt hoặc hủy khi chưa bắt đầu xử lý. Hủy có phí xử lý 50 đ.',
        icon: '✎',
        color: 'bg-orange-50 text-orange-700',
    },
    {
        title: 'Báo lỗi để làm lại',
        description: 'Với mã chung sức thành công nhưng gặp vấn đề, gửi báo lỗi để admin xem xét làm lại miễn phí.',
        icon: '↻',
        color: 'bg-violet-50 text-violet-700',
    },
    {
        title: 'Yêu cầu hoàn tiền',
        description: 'Có thể gửi yêu cầu hoàn tiền cho mã chung sức đủ điều kiện. Tiền chỉ được hoàn sau khi admin duyệt.',
        icon: '₫',
        color: 'bg-green-50 text-green-700',
    },
];

const GuestOrderLanding: React.FC = () => {
    const [pricing, setPricing] = useState<OrderPricingOption[]>([]);
    const [isLoadingPricing, setIsLoadingPricing] = useState(true);
    const [pricingError, setPricingError] = useState('');

    useEffect(() => {
        let cancelled = false;
        apiService.get<OrderPricingOption[]>('/orders/pricing')
            .then((result) => {
                if (!cancelled) setPricing(result || []);
            })
            .catch(() => {
                if (!cancelled) setPricingError('Không tải được bảng giá. Vui lòng làm mới trang để thử lại.');
            })
            .finally(() => {
                if (!cancelled) setIsLoadingPricing(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <main className="min-h-screen bg-gray-50 text-gray-900">
            <header className="border-b border-gray-200 bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="flex flex-col items-center gap-1 text-center sm:flex-row sm:gap-3" aria-label="Trang chủ">
                        <img src="/logo.webp" alt="" aria-hidden="true" className=" w-18 shrink-0 rounded-lg object-contain sm:h-10 sm:w-30" />
                        <span className="text-sm font-bold sm:text-lg">Chung sức Nhỏ TV</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/login" className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
                            Đăng nhập
                        </Link>
                        <Link to="/register" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                            Đăng ký
                        </Link>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6">
                <section className="overflow-hidden rounded-2xl bg-gradient-to-r from-blue-700 to-indigo-700 text-white shadow-sm">
                    <div className="grid gap-6 p-6 sm:p-9 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                        <div>
                            <h1 className="mt-2 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">Chung sức Nhỏ TV giá rẻ, uy tín, chất lượng hàng đầu</h1>
                            <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-blue-200">Hướng dẫn ae làm chung sức chưa tới 5p là xong</p>
                            <ol className="mt-5 grid max-w-3xl gap-2 sm:grid-cols-2">
                                {[
                                    'Tạo tài khoản và mật khẩu để đăng nhập vào web.',
                                    'Đăng nhập web (nhìn kĩ ở trên cùng sẽ có chữ Đăng nhập).',
                                    'Bấm vào phần nạp tiền, nhập số tiền muốn nạp, tạo đơn rồi quét mã để tự động duyệt. Lưu ý: Nếu nhập tay, nhớ ghi đúng nội dung chuyển khoản để được duyệt tự động.',
                                    'Vào phần nhập mã chung sức để nhập mã Chung sức của mình.',
                                    'Chọn số bước nhảy mình muốn làm (30 giây là xong 1 tài khoản).',
                                ].map((step, index) => (
                                    <li key={step} className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/10 p-3 text-sm leading-5 text-blue-50 sm:text-base">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-bold text-white">{index + 1}</span>
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ol>
                            <Link to="/register" className="mt-6 inline-flex rounded-lg bg-white px-5 py-3 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50">
                                Đăng ký để bắt đầu
                            </Link>
                        </div>
                        <div className="hidden rounded-2xl border border-white/20 bg-white/10 p-5 lg:block">
                            <p className="text-sm text-blue-100">Các chức năng mã chung sức</p>
                            <p className="mt-2 text-3xl font-bold">Tạo · Theo dõi · Hỗ trợ</p>
                            <p className="mt-1 text-sm text-blue-100">Quản lý mã tập trung sau khi đăng nhập</p>
                        </div>
                    </div>
                </section>

                <section>
                    <aside className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                        <h2 className="text-xl font-semibold">Bảng giá chung sức bên Nhỏ</h2>
                        <p className="mt-2 text-sm text-gray-700">Mức tiền nạp càng lớn càng được + thêm nhiều vé nhé ạ:</p>
                        <ul className="mt-4 space-y-2 text-sm text-gray-700">
                            {isLoadingPricing ? (
                                <li>Đang tải bảng giá...</li>
                            ) : pricingError ? (
                                <li role="alert" className="text-red-600">{pricingError}</li>
                            ) : pricing.length === 0 ? (
                                <li>Chưa có gói giá phù hợp.</li>
                            ) : pricing.map((price) => (
                                <li key={price.totalSteps}>
                                    {formatAmount(price.discountedPrice ?? price.price)} = {price.totalSteps * 38} vé
                                    {COMMUNITY_TICKET_NOTES[price.totalSteps]
                                        ? ` (${COMMUNITY_TICKET_NOTES[price.totalSteps]})`
                                        : ''}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-4 text-sm text-gray-700">Tỉ lệ nhận được skin SSS rất thấp nhé mọi người.</p>
                        <p className="mt-2 text-sm text-gray-700">Khách quay nhận được skin SSS thì quay video lại để chứng minh; bên Nhỏ sẽ tặng 1000 vé quay nhé.</p>
                    </aside>
                </section>

                <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                    <div>
                        <h2 className="text-xl font-semibold">Cách tạo mã chung sức</h2>
                        <p className="mt-1 text-sm text-gray-500">Các bước giống với giao diện quản lý mã chung sức sau khi đăng nhập.</p>
                    </div>
                    <ol className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            ['01', 'Đăng nhập hoặc đăng ký', 'Tài khoản giúp lưu mã chung sức, theo dõi tiến độ và quản lý số dư.'],
                            ['02', 'Nhập mã', 'Mỗi mã dài đúng 10 ký tự. Mỗi dòng là một mã riêng.'],
                            ['03', 'Chọn số lượt', 'Chọn gói số lượt hoặc ghi số lượt riêng sau dấu |.'],
                            ['04', 'Kiểm tra giá và tạo', 'Xem tổng tiền ước tính rồi xác nhận tạo mã chung sức trong tài khoản.'],
                        ].map(([number, title, description]) => (
                            <li key={number} className="rounded-xl bg-gray-50 p-4">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700">{number}</span>
                                <h3 className="mt-3 text-sm font-semibold text-gray-900">{title}</h3>
                                <p className="mt-1 text-sm leading-5 text-gray-600">{description}</p>
                            </li>
                        ))}
                    </ol>
                    <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                        <p className="font-semibold">Cách nhập mã hàng loạt</p>
                        <p className="mt-1">Một mã riêng lẻ sẽ dùng số lượt mặc định đã chọn. Khi nhập nhiều dòng, số lượt sau dấu | là tùy chọn và mặc định là 1.</p>
                        <p className="mt-2 font-mono">ABCDEFGHIJ</p>
                        <p className="font-mono">ABCDEFGHIJ|2</p>
                    </div>
                </section>

                <section>
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold">Xem trước danh sách mã chung sức</h2>
                        <p className="mt-1 text-sm text-gray-500">Dữ liệu minh họa để bạn hình dung cách theo dõi; mã thật chỉ hiển thị sau khi đăng nhập.</p>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="min-w-[640px] w-full text-left text-sm">
                                <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
                                    <tr>
                                        <th className="px-4 py-3">Mã chung sức</th>
                                        <th className="px-4 py-3">Trạng thái</th>
                                        <th className="px-4 py-3">Tiến độ</th>
                                        <th className="px-4 py-3 text-right">Giá</th>
                                        <th className="px-4 py-3 text-right">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {sampleOrders.map((order) => (
                                        <tr key={order.status}>
                                            <td className="px-4 py-4 font-mono text-gray-600">{order.code}</td>
                                            <td className="px-4 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${order.color}`}>{order.status}</span></td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-1.5 w-24 rounded-full bg-gray-200"><div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${order.progress}%` }} /></div>
                                                    <span className="text-xs text-gray-500">{order.progress}%</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-right text-gray-700">{order.amount}</td>
                                            <td className="px-4 py-4 text-right">
                                                <button type="button" disabled className="rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-400" title="Đăng nhập để thao tác với mã thật">Chi tiết</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">Danh sách trên chỉ là minh họa giao diện, không phải dữ liệu khách hàng thật.</p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-semibold">Các chức năng quản lý mã chung sức</h2>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        {features.map((feature) => (
                            <article key={feature.title} className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl font-bold ${feature.color}`}>{feature.icon}</span>
                                <div>
                                    <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                                    <p className="mt-1 text-sm leading-5 text-gray-600">{feature.description}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950 sm:p-6">
                    <h2 className="font-semibold">Lưu ý về phí và xử lý lỗi</h2>
                    <ul className="mt-2 list-inside list-disc space-y-1">
                        <li>Mã đang chờ có thể sửa hoặc hủy; để tránh web bị spam, từ lần hủy thứ 3 trở đi sẽ trừ phí xử lý 50 đ.</li>
                        <li>Nếu nhập sai định dạng, mã chung sức sẽ không được tạo. Nếu báo lỗi mã sai từ lần thứ 3 trở đi, sẽ trừ phí xử lý 100 đ mỗi lượt.</li>
                        <li>Mã hoàn thành không thể sửa hoặc hủy; có thể báo lỗi để admin xem xét làm lại hoặc yêu cầu hoàn tiền.</li>
                        <li>Yêu cầu hoàn tiền cần admin duyệt trước khi tiền được cộng lại vào ví.</li>
                    </ul>
                </section>

                <section className="flex flex-col items-start justify-between gap-4 rounded-2xl bg-gray-900 p-6 text-white sm:flex-row sm:items-center sm:p-8">
                    <div>
                        <h2 className="text-xl font-semibold">Sẵn sàng tạo mã chung sức?</h2>
                        <p className="mt-1 text-sm text-gray-300">Đăng nhập hoặc tạo tài khoản để gửi mã chung sức và sử dụng đầy đủ chức năng.</p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/login" className="rounded-lg border border-gray-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800">Đăng nhập</Link>
                        <Link to="/register" className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Tạo tài khoản</Link>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default GuestOrderLanding;
